import type { LanguageMode } from "./languageMode.js";

/** Filename-only special cases (no extension). */
const SPECIAL_FILENAMES: Record<string, LanguageMode> = {
  makefile: "makefile",
  gnumakefile: "makefile",
};

const EXTENSION_TO_MODE: Record<string, LanguageMode> = {
  c: "c",
  h: "cpp",
  cc: "cpp",
  cpp: "cpp",
  cxx: "cpp",
  hpp: "cpp",
  hh: "cpp",
  inl: "cpp",
  cs: "cs",
  java: "java",
  html: "html",
  htm: "html",
  xhtml: "html",
  xml: "xml",
  svg: "xml",
  xsd: "xml",
  xsl: "xml",
  wsdl: "xml",
  js: "js",
  mjs: "js",
  cjs: "js",
  jsx: "js",
  ts: "ts",
  tsx: "ts",
  php: "php",
  phtml: "php",
  py: "python",
  pyw: "python",
  pyi: "python",
  json: "json",
  jsonc: "json",
  yaml: "yaml",
  yml: "yaml",
  css: "css",
  scss: "css",
  less: "css",
  sql: "sql",
  sh: "bash",
  bash: "bash",
  zsh: "bash",
  ksh: "bash",
  ps1: "powershell",
  psm1: "powershell",
  rb: "ruby",
  rbw: "ruby",
  go: "go",
  rs: "rust",
  r: "r",
  lua: "lua",
  pl: "perl",
  pm: "perl",
  ini: "ini",
  cfg: "ini",
  reg: "ini",
  editorconfig: "ini",
  props: "props",
  properties: "props",
  md: "md",
  markdown: "md",
  mdown: "md",
  tex: "tex",
  latex: "tex",
  ltx: "tex",
  sty: "tex",
  cls: "tex",
  dtx: "tex",
  ins: "tex",
  oeis: "oeis",
  max: "max",
  lib: "linuxobj",
  a: "linuxobj",
  o: "linuxobj",
  so: "linuxobj",
  mk: "makefile",
  csv: "csv",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  pdf: "pdf",
  diff: "diff",
  patch: "diff",
  nulla: "max",
  draw: "max",
  nppd: "max",
};

/** Extensions offered in the Open dialog (`text/*` bucket). */
const EXTRA_OPEN_EXTENSIONS = [".txt", ".log", ".toml", ".gradle", ".gitignore"];

function basenameLower(pathOrName: string): string {
  const s = pathOrName.trim().replace(/\\/g, "/");
  const i = s.lastIndexOf("/");
  return (i >= 0 ? s.slice(i + 1) : s).toLowerCase();
}

/**
 * Pick `LanguageMode` from a path or display name (extension + known bare filenames).
 * Unknown → `"text"`.
 */
export function inferLanguageModeFromFilename(pathOrName: string): LanguageMode {
  const base = basenameLower(pathOrName);
  if (!base) return "text";

  const special = SPECIAL_FILENAMES[base];
  if (special) return special;

  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "text";

  const ext = base.slice(dot + 1);
  return EXTENSION_TO_MODE[ext] ?? "text";
}

/** Sorted unique extensions for `showOpenFilePicker` / `<input type="file" accept>`. */
export function recognizedSourceFileExtensions(): readonly string[] {
  const set = new Set<string>();
  for (const ext of Object.keys(EXTENSION_TO_MODE)) set.add(`.${ext}`);
  for (const x of EXTRA_OPEN_EXTENSIONS) set.add(x);
  return [...set].sort((a, b) => a.localeCompare(b));
}
